import type {
  AdminCollectionProduct,
  AdminCollectionProductsResponse,
  AdminCollectionRow,
  AssignCustomProductBody,
} from "@/interfaces/adminCollections";
import { apiClient } from "@/lib/apiClient";

interface Envelope<T> {
  success: boolean;
  data: T;
}

const assignPath = (productId: string) =>
  `/admin/custom-collection/products/${encodeURIComponent(productId)}/assign`;

/** GET /admin/collections -- every collection, in server order. */
export async function fetchAdminCollections(): Promise<AdminCollectionRow[]> {
  const { data } = await apiClient.get<Envelope<AdminCollectionRow[]>>("/admin/collections");
  return data.data;
}

/** PATCH /admin/collections/:id -- platform visibility switch. */
export async function setCollectionPlatformEnabled(
  id: string,
  enabled: boolean,
): Promise<AdminCollectionRow> {
  const { data } = await apiClient.patch<Envelope<AdminCollectionRow>>(
    `/admin/collections/${encodeURIComponent(id)}`,
    { platformEnabled: enabled },
  );
  return data.data;
}

/** GET /admin/collections/:id/products -- the product browser payload. */
export async function fetchAdminCollectionProducts(
  id: string,
): Promise<AdminCollectionProductsResponse> {
  const { data } = await apiClient.get<Envelope<AdminCollectionProductsResponse>>(
    `/admin/collections/${encodeURIComponent(id)}/products`,
  );
  return data.data;
}

/** POST /admin/custom-collection/products/:productId/assign -- upsert the org + price tier. */
export async function assignCustomProduct(
  productId: string,
  body: AssignCustomProductBody,
): Promise<AdminCollectionProduct> {
  const { data } = await apiClient.post<Envelope<AdminCollectionProduct>>(
    assignPath(productId),
    body,
  );
  return data.data;
}

/** DELETE /admin/custom-collection/products/:productId/assign -- idempotent. */
export async function unassignCustomProduct(productId: string): Promise<AdminCollectionProduct> {
  const { data } = await apiClient.delete<Envelope<AdminCollectionProduct>>(assignPath(productId));
  return data.data;
}
